import {
  Grid,
  Typography,
  Button,
  Box,
  CircularProgress,
} from "@mui/material";
import { useState, useRef } from "react";
import AssignmentIcon from "@mui/icons-material/Assignment";
import { useTheme } from "@mui/material/styles";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import Tooltip from "@mui/material/Tooltip";

import CardHeader from "../General/CardHeader";
import DynamicExcelHelpModal from "../General/DynamicExcelHelpModal";
import GlassCard from "../General/GlassCard";
import ConfirmDialog from "../General/Dialog/ConfirmDialog";
import FileUploadBox from "../General/FileUploadBox";

import { uploadDeleteNotificationCertificate } from "../../services/NotificationCertificateService";
import { useToast } from "../../context/ToastContext";

export default function ChargeFileDeleteNotificationCertificate() {
  const { showToast } = useToast();
  const theme = useTheme();
  const fileInputRef = useRef(null);

  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({ file: "" });
  const [errors, setErrors] = useState({});
  const [selectedFile, setSelectedFile] = useState(null);

  const [openConfirm, setOpenConfirm] = useState(false);
  const [openHelp, setOpenHelp] = useState(false);

  const handleOpenHelp = () => setOpenHelp(true);
  const handleCloseHelp = () => setOpenHelp(false);

  const resetForm = () => {
    setForm({ file: "" });
    setSelectedFile(null);
    setErrors({});

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleFileSelect = (file) => {
    if (file) {
      const validExtensions = ["xls", "xlsx"];
      const fileExtension = file.name.split(".").pop().toLowerCase();

      if (!validExtensions.includes(fileExtension)) {
        setErrors({
          ...errors,
          file: "Solo se permiten archivos .xls o .xlsx",
        });
        setSelectedFile(null);
        setForm({ ...form, file: "" });
        return;
      }

      setErrors({ ...errors, file: "" });
      setSelectedFile(file);
      setForm({ ...form, file: file.name });
    }
  };

  const handleSubmit = async () => {    setErrors({});
    setLoading(true);

    if (!selectedFile) {
      showToast("Debe seleccionar un archivo.", "info")
      setLoading(false);
      return;
    }

    const formData = new FormData();
    formData.append("file", selectedFile);

    try {
      const response = await uploadDeleteNotificationCertificate(formData);

      showToast(response.data.message, "success")
      resetForm();
      setOpenConfirm(false);
    } catch (error) {
      if (error.response?.status === 422 || error.response?.status === 404) {
        const backendErrors = error.response.data?.errors;

        if (Array.isArray(backendErrors) && backendErrors.length > 0) {
          showToast(
            `${error.response.data?.message || "Ocurrió un error."}\n${backendErrors.join("\n")}`,
          "error")
        } else {
          showToast(error.response.data?.message || "Ocurrió un error.", "error")
        }
      } else {
        console.error(error);
        showToast("Ocurrió un error inesperado.", "error")
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();

    if (!selectedFile) {
      showToast("Debe seleccionar un archivo.", "info")
      return;
    }

    setOpenConfirm(true);
  };

  return (
    <Grid container justifyContent="center" sx={{ mt: 4 }}>
      <Grid size={{ xs: 12, sm: 12, md: 12, lg: 10 }}>

        <GlassCard>
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Tooltip title="Ver requisitos del Excel" arrow>
              <HelpOutlineIcon
                sx={{
                  cursor: "pointer",
                  color: theme.palette.primary.main,
                  "&:hover": { color: theme.palette.primary.dark },
                }}
                onClick={handleOpenHelp}
              />
            </Tooltip>
          </Box>

          <CardHeader
            icon={<AssignmentIcon />}
            title="Eliminación de Cédulas"
            description="Elimina cédulas de notificación mediante archivo Excel."
          />

          <Box component="form" onSubmit={handleFormSubmit}>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12 }}>
                <FileUploadBox
                  selectedFile={selectedFile}
                  onFileSelect={handleFileSelect}
                  accept=".xls,.xlsx"
                  placeholder="Seleccionar Excel para Eliminar"
                  helperText="O arrastrá el Excel aquí (.xls, .xlsx)"
                />

                {errors.file && (
                  <Typography
                    color="error"
                    variant="caption"
                    sx={{ mt: 1, display: "block" }}
                  >
                    {errors.file}
                  </Typography>
                )}
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Button
                  variant="contained"
                  type="submit"
                  fullWidth
                  disabled={loading}
                 
                >
                  {loading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    "Eliminar Cédulas"
                  )}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </GlassCard>
      </Grid>

      <ConfirmDialog
        open={openConfirm}
        onClose={() => setOpenConfirm(false)}
        onConfirm={handleSubmit}
        title="Confirmar eliminación"
        description="¿Estás seguro de que quieres eliminar las cédulas cargadas en el archivo? Esta acción no se puede deshacer."
        confirmText="Eliminar"
        cancelText="Cancelar"
        confirmColor="error"
        loading={loading}
      />

      <DynamicExcelHelpModal
        open={openHelp}
        onClose={handleCloseHelp}
        title="Requisitos del archivo Excel para eliminar cédulas"
        description="El archivo debe ser formato .xls o .xlsx y contener únicamente el código único de la cédula en la primera columna."
        columns={["Código"]}
        example={["S1/11561831"]}
      />
    </Grid>
  );
}
